import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { getRouteApi } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';

import { ExpressionDetailsComponent } from '#components/expression-details/expression-details.js';
import { useAbilityContext } from '#src/state/contexts/ability-context.js';
import { AnalysisResultsModuleChip } from '../components/analysis-results-module-chip.js';
import { SubtableToolbar } from '../components/subtable-toolbar.js';
import { PackageStoreLink } from '../package-store-link.js';

import type {
	ModuleSignalDriveMode,
	NetDriveCompatibilityRequirementResultDetails,
} from '@cadstrom/sigma-engine-shared';
import type {
	DataGridPremiumProps,
	GridColDef,
	GridFilterModel,
	GridRowClassNameParams,
	GridRowHeightParams,
	GridToolbarProps,
} from '@mui/x-data-grid-premium';

export type NetDriveDetailTableProps = {
	/**
	 * Validated NetDrive metadata (`instanceMetadata.type ===
	 * 'NetDriveCompatibilityResult'`).
	 */
	instanceMetadata: NetDriveCompatibilityRequirementResultDetails;
	loading?: boolean;
	/**
	 * Optional click-through for the module ref-des chip menu. Omit when the
	 * table is rendered outside the analysis-results grid (e.g. inside a V2
	 * activity-timeline payload accordion) — the menu item is dropped in that
	 * case.
	 */
	handleAddModuleFilter?: (moduleReferenceDesignator: string) => void;
};

type NetDriveToolbarProps = GridToolbarProps & {
	netName?: string;
	showActiveOnly?: boolean;
	onActiveToggle?: () => void;
	activeSummary?: { active: number; inactive: number; unresolved: number };
};

function NetDriveToolbar({
	netName,
	showActiveOnly = false,
	onActiveToggle = () => {
		// Default no-op function for toolbar
	},
	activeSummary = { active: 0, inactive: 0, unresolved: 0 },
}: NetDriveToolbarProps) {
	return (
		<SubtableToolbar
			title=''
			subtitle=''
			net={netName}
			showActiveToggle
			showActiveOnly={showActiveOnly}
			onActiveToggle={onActiveToggle}
			activeSummary={activeSummary}
		/>
	);
}

const routeApi = getRouteApi('/_authorization-gate/orgs/$orgName/projects/$projectId/revisions/$revisionId/results');

/**
 * The actual table body for a NetDriveCompatibilityResult. Extracted from
 * `NetDriveDetailPanel` so both the legacy V1 detail-panel route and the V2
 * activity-timeline payload accordion render identical output from the same
 * `instanceMetadata` shape.
 */
export function NetDriveDetailTable({
	instanceMetadata,
	loading = false,
	handleAddModuleFilter,
}: NetDriveDetailTableProps) {
	const { projectId, revisionId: buildId } = routeApi.useParams();
	const ability = useAbilityContext();
	const canManagePackages = ability.can('manage', 'package');

	const [filterModel, setFilterModel] = useState<GridFilterModel>({
		items:
			instanceMetadata.modules.length > 5 ?
				[
					{
						field: 'modeActiveState',
						operator: 'is',
						value: 'Active',
					},
				]
			:	[],
	});

	let idCounter = 0;
	const allDetailRows = useMemo(() => {
		return instanceMetadata.modules.map((module) => ({
			id: `${module.moduleId}::${module.signalId ?? 'no-signal'}::${idCounter++}`,
			...module,
		}));
	}, [idCounter, instanceMetadata.modules]);

	const activeSummary = useMemo(() => {
		const active = allDetailRows.filter((row) => row.modeActiveState === 'Active').length;
		const inactive = allDetailRows.filter((row) => row.modeActiveState === 'Inactive').length;
		const unresolved = allDetailRows.filter((row) => row.modeActiveState === 'Unresolved').length;
		return { active, inactive, unresolved };
	}, [allDetailRows]);

	const showActiveOnly = useMemo(() => {
		return filterModel.items.some(
			(item) => item.field === 'modeActiveState' && item.operator === 'is' && item.value === 'Active',
		);
	}, [filterModel]);

	const handleActiveToggle = useCallback(() => {
		setFilterModel((prev) => {
			const hasActiveFilter = prev.items.some(
				(item) => item.field === 'modeActiveState' && item.operator === 'is' && item.value === 'Active',
			);

			if (hasActiveFilter) {
				return {
					...prev,
					items: prev.items.filter(
						(item) => !(item.field === 'modeActiveState' && item.operator === 'is' && item.value === 'Active'),
					),
				};
			}
			return {
				...prev,
				items: [
					...prev.items.filter((item) => item.field !== 'modeActiveState'),
					{
						field: 'modeActiveState',
						operator: 'is',
						value: 'Active',
					},
				],
			};
		});
	}, []);

	const detailPanelColumns: GridColDef<ModuleSignalDriveMode & { id: string }>[] = useMemo(
		() => [
			{
				field: 'moduleId',
				headerName: 'Ref Des',
				display: 'flex',
				flex: 1,
				filterable: true,
				sortable: true,
				renderCell: ({ row }) => (
					<AnalysisResultsModuleChip
						moduleReferenceDesignator={row.moduleId}
						handleAddModuleFilter={handleAddModuleFilter}
					/>
				),
			} satisfies GridColDef<ModuleSignalDriveMode & { id: string }, ModuleSignalDriveMode['moduleId']>,
			{
				field: 'signalId',
				headerName: 'Signal ID',
				flex: 1,
				filterable: true,
				sortable: true,
				renderCell: ({ value, row }) => {
					if (value === null || value === undefined) {
						return <div className='text-gray-a8 italic'>No signal</div>;
					}
					return canManagePackages ?
							<PackageStoreLink
								projectId={projectId}
								buildId={buildId}
								referenceDesignator={row.moduleId}
								displayText={value}
								toTab='signal-drive-modes'
							/>
						:	<span>{value}</span>;
				},
			} satisfies GridColDef<ModuleSignalDriveMode & { id: string }, ModuleSignalDriveMode['signalId']>,
			{
				field: 'driveType',
				headerName: 'Drive Type',
				flex: 1,
				filterable: true,
				sortable: true,
			} satisfies GridColDef<ModuleSignalDriveMode & { id: string }, ModuleSignalDriveMode['driveType']>,
			{
				field: 'outputLowVoltage',
				headerName: 'VOL',
				flex: 1,
				filterable: true,
				sortable: true,
				renderCell: ({ row }) => <ExpressionDetailsComponent details={row.outputLowVoltageDetails} />,
			} satisfies GridColDef<ModuleSignalDriveMode & { id: string }>,
			{
				field: 'outputHighVoltage',
				headerName: 'VOH',
				flex: 1,
				filterable: true,
				sortable: true,
				renderCell: ({ row }) => <ExpressionDetailsComponent details={row.outputHighVoltageDetails} />,
			} satisfies GridColDef<ModuleSignalDriveMode & { id: string }>,
			{
				field: 'inputLowVoltage',
				headerName: 'VIL',
				flex: 1,
				filterable: true,
				sortable: true,
				renderCell: ({ row }) => <ExpressionDetailsComponent details={row.inputLowVoltageDetails} />,
			} satisfies GridColDef<ModuleSignalDriveMode & { id: string }>,
			{
				field: 'inputHighVoltage',
				headerName: 'VIH',
				flex: 1,
				filterable: true,
				sortable: true,
				renderCell: ({ row }) => <ExpressionDetailsComponent details={row.inputHighVoltageDetails} />,
			} satisfies GridColDef<ModuleSignalDriveMode & { id: string }>,
			{
				field: 'modeActiveState',
				headerName: 'Threshold Status',
				flex: 1,
				type: 'singleSelect',
				valueOptions: ['Active', 'Inactive', 'Unresolved'],
				filterable: true,
				sortable: true,
			} satisfies GridColDef<ModuleSignalDriveMode & { id: string }, ModuleSignalDriveMode['modeActiveState']>,
			{
				field: 'driveModeConditionNames',
				headerName: 'Conditions',
				flex: 1,
				filterable: true,
				sortable: true,
				renderCell: ({ row }) => {
					const value = row.driveModeConditionNames;
					if (!value || value.length === 0) {
						return <div></div>;
					}
					return <div>{value.join(', ')}</div>;
				},
			} satisfies GridColDef<ModuleSignalDriveMode & { id: string }, ModuleSignalDriveMode['driveModeConditionNames']>,
		],
		[projectId, buildId, handleAddModuleFilter, canManagePackages],
	);

	const getRowClassName = ({ row }: GridRowClassNameParams<ModuleSignalDriveMode & { id: string }>) => {
		if (row.modeActiveState === 'Active') {
			return 'net-drive-active-mode';
		}
		return '';
	};

	const getRowHeight = (params: GridRowHeightParams) => {
		const model = params.model as ModuleSignalDriveMode & { id: string };
		const hasVariables =
			(model.outputLowVoltageDetails?.variables && model.outputLowVoltageDetails.variables.length > 0) ??
			(model.outputHighVoltageDetails?.variables && model.outputHighVoltageDetails.variables.length > 0) ??
			(model.inputLowVoltageDetails?.variables && model.inputLowVoltageDetails.variables.length > 0) ??
			(model.inputHighVoltageDetails?.variables && model.inputHighVoltageDetails.variables.length > 0);

		return hasVariables ? 52 : 42;
	};

	const netName = instanceMetadata.net;
	const biasVoltage = instanceMetadata.biasVoltage;
	const errors = instanceMetadata.errors;

	const slots = useMemo(
		() =>
			({
				toolbar: NetDriveToolbar,
			}) as DataGridPremiumProps<ModuleSignalDriveMode & { id: string }>['slots'],
		[],
	);

	const slotProps: DataGridPremiumProps<ModuleSignalDriveMode & { id: string }>['slotProps'] = useMemo(
		() => ({
			toolbar: {
				netName,
				showActiveOnly,
				onActiveToggle: handleActiveToggle,
				activeSummary,
			} satisfies NetDriveToolbarProps,
		}),
		[netName, showActiveOnly, handleActiveToggle, activeSummary],
	);

	return (
		<Paper className='w-full overflow-auto p-4'>
			<style>
				{`
					.net-drive-active-mode {
						background-color: color-mix(in oklab, var(--color-green-3), transparent 70%) !important;
					}
					.net-drive-active-mode:hover {
						background-color: color-mix(in oklab, var(--color-green-4), transparent 60%) !important;
					}
				`}
			</style>
			{(typeof biasVoltage === 'string' || (errors && errors.length > 0)) && (
				<div className='mb-4 space-y-2'>
					{typeof biasVoltage === 'string' && (
						<div className='flex items-center gap-2'>
							<span className='text-sm font-medium'>Bias Voltage:</span>
							<span className='text-gray-700 text-sm'>{biasVoltage}</span>
						</div>
					)}
					{errors && errors.length > 0 && (
						<div className='space-y-1'>
							<span className='text-red-600 text-sm font-medium'>Errors:</span>
							{errors.map((error) => (
								<Alert key={`${error.type}-${error.message}`} severity='error' className='text-sm'>
									<span className='text-sm'>
										<strong>{error.type}:</strong> {error.message}
									</span>
								</Alert>
							))}
						</div>
					)}
				</div>
			)}

			<DataGridPremium
				hideFooter
				loading={loading && allDetailRows.length === 0}
				disableColumnMenu
				rows={allDetailRows}
				columns={detailPanelColumns}
				getRowClassName={getRowClassName}
				getRowHeight={getRowHeight}
				disableRowSelectionOnClick
				scrollEndThreshold={24}
				showToolbar
				slots={slots}
				slotProps={slotProps}
				filterModel={filterModel}
				onFilterModelChange={setFilterModel}
				filterMode='client'
			/>
		</Paper>
	);
}
